import { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

export default function QuoteGeneratorDialog({ opportunityId, companyName, open, onOpenChange }) {
  const [frequency, setFrequency] = useState("5x / semaine (nettoyage de soir)");
  const [duration, setDuration] = useState(12);
  const [services, setServices] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const resp = await api.post(
        `/opportunities/${opportunityId}/generate-quote`,
        {
          frequency,
          duration_months: Number(duration) || 12,
          services_included: services ? services.split(",").map(s => s.trim()).filter(Boolean) : null,
          special_notes: notes || null,
        },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const quoteId = resp.headers["x-quote-id"] || "soumission";
      const a = document.createElement("a");
      a.href = url;
      a.download = `soumission_${(companyName || "client").replace(/\s+/g, "_").slice(0, 40)}_${quoteId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success("Soumission PDF générée", { description: `N° ${quoteId}` });
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de génération");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card border-border" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileText className="h-5 w-5 text-cyan-400" /> Générer une soumission
          </DialogTitle>
          <DialogDescription>
            L'IA prépare une soumission PDF professionnelle pour {companyName || "ce prospect"}. Vérifiez et ajustez avant l'envoi.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Fréquence de service</Label>
            <Input value={frequency} onChange={(e) => setFrequency(e.target.value)} className="mt-1.5" data-testid="quote-frequency-input" />
          </div>
          <div>
            <Label>Durée du contrat (mois)</Label>
            <Input type="number" min={1} max={60} value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1.5" data-testid="quote-duration-input" />
          </div>
          <div>
            <Label>Services inclus (optionnel, séparés par virgule)</Label>
            <Input
              value={services}
              onChange={(e) => setServices(e.target.value)}
              className="mt-1.5"
              placeholder="Ex: Nettoyage bureaux, entretien salles de bain, cuisinette"
              data-testid="quote-services-input"
            />
          </div>
          <div>
            <Label>Notes spéciales (optionnel)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1.5"
              placeholder="Ex: Bâtiment classé, accès sécurisé, produits écologiques exigés…"
              data-testid="quote-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Annuler</Button>
          <Button
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950"
            onClick={generate}
            disabled={loading}
            data-testid="quote-generate-button"
          >
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Génération…</> : <><Download className="h-4 w-4 mr-2" /> Générer le PDF</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
